import React, { useState, useEffect, useCallback } from 'react';
import { phoneContactsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Phone, Plus, Pencil, Trash2, X, Check, Search, Copy, ChevronDown } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const STATUS_CONFIG = {
  available:   { label: 'Available',   dot: 'bg-emerald-400 shadow-emerald-400/60', ring: 'border-emerald-400/30', pulse: true  },
  busy:        { label: 'Busy',        dot: 'bg-amber-400 shadow-amber-400/60',     ring: 'border-amber-400/30',   pulse: false },
  unavailable: { label: 'Unavailable', dot: 'bg-red-500 shadow-red-500/60',         ring: 'border-red-500/30',     pulse: false },
};

const PRESET_COLORS = ['#06b6d4','#3b82f6','#8b5cf6','#ec4899','#f97316','#22c55e','#ef4444','#eab308'];

/* ── Blank form ─────────────────────────────────────────────────────────── */
const BLANK = { role_name: '', phone_number: '', person_name: '', organisation: '', color: '#06b6d4', status: 'available' };

/* ── Contact Card ───────────────────────────────────────────────────────── */
function ContactCard({ contact, canManage, onEdit, onDelete, onStatusChange, hidePersonName = false }) {
  const sc = STATUS_CONFIG[contact.status] || STATUS_CONFIG.available;
  const accent = contact.color || '#06b6d4';

  const copyNumber = () => {
    navigator.clipboard.writeText(contact.phone_number);
    toast.success(`Copied ${contact.phone_number}`);
  };

  return (
    <div
      className={`relative rounded-xl border p-4 transition-all duration-300 hover:scale-[1.02] group bg-card`}
      style={{ borderColor: `${accent}30` }}
      data-testid={`phone-contact-${contact.contact_id}`}
    >
      {/* Status dot */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full shadow-md ${sc.dot} ${sc.pulse ? 'animate-pulse' : ''}`} />
          <span className="text-xs text-muted-foreground font-medium">{sc.label}</span>
        </div>
        {canManage && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <select
              value={contact.status}
              onChange={e => onStatusChange(contact.contact_id, e.target.value)}
              className="text-[10px] bg-muted border rounded px-1 py-0.5 text-muted-foreground cursor-pointer"
              onClick={e => e.stopPropagation()}
              data-testid={`status-select-${contact.contact_id}`}
            >
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="unavailable">Unavailable</option>
            </select>
            <button onClick={() => onEdit(contact)}
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid={`edit-contact-${contact.contact_id}`}>
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => onDelete(contact.contact_id)}
              className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
              data-testid={`delete-contact-${contact.contact_id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Role name */}
      <h3 className="font-bold text-sm text-foreground leading-tight mb-0.5">
        {contact.role_name}
      </h3>

      {/* Person name */}
      {!hidePersonName && contact.person_name && (
        <p className="text-xs text-muted-foreground mb-2">{contact.person_name}</p>
      )}

      {/* Organisation */}
      {contact.organisation && (
        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full border mb-2"
          style={{ borderColor: `${accent}40`, color: accent, background: `${accent}10` }}>
          {contact.organisation}
        </span>
      )}

      {/* Phone number */}
      <div className="flex items-center gap-2 mt-2">
        <div className="flex-1 flex items-center gap-2 bg-muted rounded-lg px-3 py-2 border">
          <Phone className="w-3 h-3 flex-shrink-0" style={{ color: accent }} />
          <span className="font-mono text-sm font-semibold tracking-widest" style={{ color: accent }}>
            {contact.phone_number}
          </span>
        </div>
        <button
          onClick={copyNumber}
          className="p-2 rounded-lg bg-muted border text-muted-foreground hover:text-foreground hover:border-border transition-all"
          title="Copy number"
          data-testid={`copy-number-${contact.contact_id}`}>
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── Compact Contact Row (for narrow containers like participant dashboard) ── */
function ContactRow({ contact, onCopy }) {
  const sc = STATUS_CONFIG[contact.status] || STATUS_CONFIG.available;
  const accent = contact.color || '#06b6d4';

  const copyNumber = () => {
    navigator.clipboard.writeText(contact.phone_number);
    toast.success(`Copied ${contact.phone_number}`);
  };

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 hover:bg-muted/40 transition-colors group"
      data-testid={`phone-contact-${contact.contact_id}`}
    >
      {/* Status dot */}
      <span className={`w-2 h-2 rounded-full flex-shrink-0 shadow-sm ${sc.dot} ${sc.pulse ? 'animate-pulse' : ''}`} />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-xs text-foreground truncate">{contact.role_name}</span>
          {contact.organisation && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0"
              style={{ borderColor: `${accent}40`, color: accent, background: `${accent}08` }}>
              {contact.organisation}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Phone className="w-2.5 h-2.5 flex-shrink-0" style={{ color: accent }} />
          <span className="font-mono text-[11px] font-medium" style={{ color: accent }}>
            {contact.phone_number}
          </span>
        </div>
      </div>

      {/* Copy */}
      <button
        onClick={copyNumber}
        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
        title="Copy number"
        data-testid={`copy-number-${contact.contact_id}`}>
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ── Edit/Create Form ───────────────────────────────────────────────────── */
function ContactForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || BLANK);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3 shadow-sm">
      <p className="text-sm font-semibold text-primary flex items-center gap-2">
        {initial ? <><Pencil className="w-3.5 h-3.5" /> Edit Contact</> : <><Plus className="w-3.5 h-3.5" /> New Exercise Contact</>}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Role Name *</label>
          <Input value={form.role_name} onChange={e => set('role_name', e.target.value)}
            placeholder="e.g. Fire Chief, Coast Guard" className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Phone Number *</label>
          <Input value={form.phone_number} onChange={e => set('phone_number', e.target.value)}
            placeholder="e.g. +44 7700 900000" className="h-8 text-sm font-mono" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Person Name</label>
          <Input value={form.person_name || ''} onChange={e => set('person_name', e.target.value)}
            placeholder="Optional" className="h-8 text-sm" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Organisation</label>
          <Input value={form.organisation || ''} onChange={e => set('organisation', e.target.value)}
            placeholder="Optional" className="h-8 text-sm" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Status</label>
          <select value={form.status} onChange={e => set('status', e.target.value)}
            className="w-full h-8 text-sm bg-background border rounded-md px-2 text-foreground">
            <option value="available">Available</option>
            <option value="busy">Busy</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Accent Color</label>
          <div className="flex items-center gap-1 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => set('color', c)}
                className={`w-5 h-5 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-1 ring-primary scale-110' : 'opacity-60 hover:opacity-100'}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
              className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent" style={{ padding: 0 }} />
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button size="sm" className="flex-1 h-8 text-xs"
          onClick={() => onSave(form)} disabled={!form.role_name.trim() || !form.phone_number.trim()}
          data-testid="contact-form-save">
          <Check className="w-3.5 h-3.5 mr-1" /> Save
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs"
          onClick={onCancel}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}

/* ── Main PhoneDirectory ────────────────────────────────────────────────── */
export default function PhoneDirectory({ exerciseId, compact = false }) {
  const { isAdmin, user } = useAuth();
  const canManage = isAdmin() || user?.roles?.includes('simcel');
  const isParticipant = user?.roles?.includes('participant');

  const [contacts,  setContacts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [adding,    setAdding]    = useState(false);
  const [editing,   setEditing]   = useState(null);   // contact object being edited
  const [groupBy,   setGroupBy]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    phoneContactsApi.list(exerciseId)
      .then(d => setContacts(d || []))
      .catch(() => toast.error('Failed to load directory'))
      .finally(() => setLoading(false));
  }, [exerciseId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    try {
      const c = await phoneContactsApi.create(exerciseId, form);
      setContacts(p => [...p, c]);
      setAdding(false);
      toast.success(`"${c.role_name}" added`);
    } catch { toast.error('Failed to create contact'); }
  };

  const handleUpdate = async (form) => {
    try {
      const c = await phoneContactsApi.update(exerciseId, editing.contact_id, form);
      setContacts(p => p.map(x => x.contact_id === c.contact_id ? c : x));
      setEditing(null);
      toast.success('Contact updated');
    } catch { toast.error('Failed to update contact'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await phoneContactsApi.delete(exerciseId, id);
      setContacts(p => p.filter(x => x.contact_id !== id));
      toast.success('Contact deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const c = await phoneContactsApi.updateStatus(exerciseId, id, status);
      setContacts(p => p.map(x => x.contact_id === id ? c : x));
    } catch { toast.error('Failed to update status'); }
  };

  const filtered = contacts.filter(c =>
    !search || [c.role_name, c.person_name, c.organisation, c.phone_number]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  // Group by organisation
  const grouped = groupBy
    ? filtered.reduce((acc, c) => {
        const key = c.organisation || 'Unassigned';
        if (!acc[key]) acc[key] = [];
        acc[key].push(c);
        return acc;
      }, {})
    : { All: filtered };

  const stats = {
    total: contacts.length,
    available: contacts.filter(c => c.status === 'available').length,
    busy: contacts.filter(c => c.status === 'busy').length,
  };

  return (
    <div className="h-full flex flex-col space-y-4"
      style={{ background: 'transparent' }}>

      {/* ── Header ── */}
      <div className={compact ? "space-y-1.5" : "flex items-center justify-between"}>
        <div className={compact ? "" : "flex items-center gap-3"}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
              <Phone className="w-3 h-3 text-cyan-400" />
            </div>
            <span className={`font-bold text-foreground tracking-wide ${compact ? 'text-xs' : 'text-sm'}`}>
              {compact ? 'CONTACTS' : 'EXERCISE CONTACT LIST'}
            </span>
          </div>
          {/* Live stats */}
          <div className="flex items-center gap-1.5 text-[10px] mt-1">
            <span className="px-1.5 py-0.5 rounded-full bg-card border border-border text-muted-foreground">
              {stats.total}
            </span>
            {stats.available > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {stats.available}
              </span>
            )}
            {stats.busy > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
                {stats.busy}
              </span>
            )}
          </div>
        </div>
        {canManage && !adding && !editing && (
          <Button size="sm"
            className={`h-7 text-xs gap-1 ${compact ? 'mt-1.5 w-full' : ''}`}
            onClick={() => setAdding(true)}
            data-testid="add-contact-btn">
            <Plus className="w-3 h-3" /> Add
          </Button>
        )}
      </div>

      {/* ── Create / Edit form ── */}
      {adding && <ContactForm onSave={handleCreate} onCancel={() => setAdding(false)} />}
      {editing && <ContactForm initial={editing} onSave={handleUpdate} onCancel={() => setEditing(null)} />}

      {/* ── Search + group toggle ── */}
      {contacts.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search role, name, number…"
              className="pl-8 h-8 text-xs" />
          </div>
          <Button size="sm" variant={groupBy ? 'default' : 'ghost'}
            className="h-8 text-xs px-2"
            onClick={() => setGroupBy(v => !v)}>
            <ChevronDown className="w-3 h-3 mr-1" /> Group
          </Button>
        </div>
      )}

      {/* ── Contact grid / list ── */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">Loading…</div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 space-y-3 text-center">
            <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-cyan-500/50" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">No exercise contacts yet</p>
              {canManage && (
                <p className="text-xs text-muted-foreground mt-0.5">Click "Add Contact" to build the phone directory</p>
              )}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">No contacts match "{search}"</div>
        ) : compact ? (
          /* Compact list layout for narrow containers */
          <div className="border rounded-lg overflow-hidden bg-card divide-y">
            {filtered.map(c => (
              <ContactRow key={c.contact_id} contact={c} />
            ))}
          </div>
        ) : (
          Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              {groupBy && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] text-muted-foreground uppercase tracking-widest px-2">{group}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map(c => (
                  <ContactCard
                    key={c.contact_id}
                    contact={c}
                    canManage={canManage}
                    hidePersonName={isParticipant}
                    onEdit={setEditing}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
